import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp } from 'lucide-react';
import { floatingSurfaceClass } from '../floatingSurfaceStyles';
import { useTheme } from '../../../context/ThemeContext';
import { availableGrowingStatusGroups, growingStatusColor } from './growingStatuses';
import { PinGlyph, SwatchStack } from './legendGlyphs';

const SURFACE = floatingSurfaceClass(95);

/** Panel and toggle share one width so the toggle cannot sit proud of the panel. */
const LEGEND_W = 'w-[131px]';

/**
 * "Region status" key for the Growing layer — what the pin colours mean.
 *
 * Rows, their order and the divider all come from `growingStatuses`, filtered
 * to the statuses the data can carry. A status with no data source (`peak`,
 * `disease` today) is defined there but never rendered here, so the key can
 * never promise a colour the map has no way to draw.
 *
 * Collapsible, and anchored bottom-left by its caller: on the right it sat
 * over the layer rail. Collapsed it is just the 28px toggle; expanded the
 * panel stacks above the toggle, so the toggle itself never moves and the
 * legend grows away from the map's bottom edge rather than through it.
 */
const RegionStatusLegend = ({ className = '', defaultOpen = false }) => {
  // Closed by default on every layer: the map is the content, and a key
  // the user has not asked for should not cover it on arrival. The prop
  // stays so a caller can still open one deliberately.
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  const groups = availableGrowingStatusGroups();
  const label = t('map.growing.legendTitle', 'Region status');

  return (
    <div className={`flex ${LEGEND_W} flex-col items-stretch gap-[4px] ${className}`}>
      {open && (
        <div className={`rounded-[12px] border border-solid px-[12px] py-[12px] ${SURFACE}`}>
          <p className="font-['Inter'] text-[9px] font-extrabold uppercase tracking-[0.63px] text-[#9CA3AF] dark:text-[#6B716C]">
            {label}
          </p>

          {groups.map((group, i) => (
            <React.Fragment key={group[0].id}>
              {i > 0 && <div className="my-[8px] h-px w-full bg-[#EEF0EC] dark:bg-[#2A302C]" />}
              <div className={`flex flex-col gap-[7px] ${i === 0 ? 'mt-[8px]' : ''}`}>
                {group.map((status) => (
                  <div key={status.id} className="flex items-center gap-[7px]">
                    <PinGlyph color={status.color} isDark={isDark} />
                    <span className="font-['Inter'] text-[11px] leading-[15px] whitespace-nowrap text-[#282A29] dark:text-[#E6E8E6]">
                      {t(`map.filters.productionStatus.${status.id}`, status.label)}
                    </span>
                  </div>
                ))}
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Toggle. Last in DOM order so it stays put as the panel opens upward. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex h-[28px] w-full items-center rounded-[14px] border border-solid border-[#E6E9E4] bg-white pl-[10px] pr-[8px] drop-shadow-[0px_1px_1.5px_rgba(17,24,39,0.07)] dark:border-[#2A302C] dark:bg-[#1B1F1C]"
      >
        <span className="font-['Inter'] text-[10px] font-semibold whitespace-nowrap text-[#343440] dark:text-[#E6E8E6]">
          {label}
        </span>
        <SwatchStack colors={['producing', 'starting_soon', 'not_producing'].map(growingStatusColor)} />
        <ChevronUp
          size={10}
          className={`shrink-0 text-[#5D625F] transition-transform dark:text-[#9AA09A] ${open ? '' : 'rotate-180'}`}
        />
      </button>
    </div>
  );
};

export default RegionStatusLegend;
